/**
 * Single-line text field with optional leading icon, label, hint and error message.
 * Spec: assets/handoffs/design_system_export/specs/Input.json
 *
 * @param {'sm'|'md'=md} size
 * @param {string=} label
 * @param {string=} hint
 * @param {string=} error
 * @param {ReactNode=} iconLeading
 * @param {string=} placeholder
 * @param {boolean=false} disabled
 * @param {boolean=false} readOnly
 * @param {boolean=false} required
 */
import React, { forwardRef, useId } from "react";
import classNames from "classnames";
import styles from "./Input.module.scss";

const Input = forwardRef(
  (
    {
      size = "md",
      label,
      hint,
      error,
      iconLeading = null,
      disabled = false,
      readOnly = false,
      required = false,
      className,
      id,
      ...rest
    },
    ref
  ) => {
    const autoId = useId();
    const inputId = id || autoId;
    const hintId = hint ? `${inputId}-hint` : undefined;
    const errorId = error ? `${inputId}-error` : undefined;
    const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;

    return (
      <div className={classNames(styles.root, className)}>
        {label && (
          <label className={styles.label} htmlFor={inputId}>
            {label}
            {required && <span aria-hidden="true"> *</span>}
          </label>
        )}
        <div
          className={classNames(styles.field, styles[`field--${size}`], {
            [styles["is-error"]]: !!error,
            [styles["is-disabled"]]: disabled,
            [styles["has-icon"]]: !!iconLeading,
          })}
        >
          {iconLeading && (
            <span className={styles.icon} aria-hidden="true">
              {iconLeading}
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            className={styles.input}
            disabled={disabled}
            readOnly={readOnly}
            required={required}
            aria-invalid={error ? "true" : undefined}
            aria-describedby={describedBy}
            {...rest}
          />
        </div>
        {error ? (
          <span id={errorId} className={styles.errorText}>
            {error}
          </span>
        ) : (
          hint && (
            <span id={hintId} className={styles.hint}>
              {hint}
            </span>
          )
        )}
      </div>
    );
  }
);

Input.displayName = "Input";

export default Input;
